import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import { PageHeader } from '../../components/ui/PageHeader';
import { PageToolbar } from '../../components/ui/PageToolbar';
import { PageNotificationToast } from '../../components/notifications/PageNotificationToast';
import { PricingUpdateListRow } from '../../components/forms/PricingUpdateListRow';
import { usePageNotifications } from '../../hooks/usePageNotifications';
import { pageFeedbackToToast } from '../../lib/pageFeedbackToast';
import {
  currentPufYearMonth,
  fetchPricingUpdateSubmissions,
  pufYearMonthFromApplicationNo,
} from '../../lib/pricingUpdateSubmissions';
import { STATUS_LABEL } from '../../lib/accountCreationUi';
import { usePortal } from '../../contexts/PortalContext';
import { useFormsSubmissionsRealtime } from '../../hooks/useFormsSubmissionsRealtime';
import type { ApplicationSubmission, PricingUpdateFormData } from '../../types/applicationForm';

const MONTH_VALUES = ['01', '02', '03', '04', '05', '06', '07', '08', '09', '10', '11', '12'] as const;

function parseYearMonth(yearMonth: string): { year: string; month: string } {
  const m = /^(\d{4})-(\d{2})$/.exec(yearMonth.trim());
  if (m) return { year: m[1], month: m[2] };
  const fallback = currentPufYearMonth();
  return { year: fallback.slice(0, 4), month: fallback.slice(5, 7) };
}

function monthShortLabel(month: string): string {
  const date = new Date(`2020-${month}-15T12:00:00+08:00`);
  return new Intl.DateTimeFormat('en-SG', {
    month: 'short',
    timeZone: 'Asia/Singapore',
  }).format(date);
}

function monthLabel(yearMonth: string): string {
  const { year, month } = parseYearMonth(yearMonth);
  return `${monthShortLabel(month)}'${year}`;
}

function pufSearchBlob(row: ApplicationSubmission): string {
  const data = row.form_data as PricingUpdateFormData;
  const items = (data?.items ?? []).map((item) => `${item.item_no} ${item.item_description}`).join(' ');
  return [
    row.application_no,
    row.company_name,
    data?.customer_no,
    data?.customer_name,
    data?.customer_price_group,
    data?.reason_requestor,
    items,
    row.submitted_by,
    row.authorised_by,
    row.approved_by,
    row.rejected_by,
    row.status,
    STATUS_LABEL[row.status],
  ]
    .map((v) => String(v ?? '').toLowerCase())
    .join('\n');
}

type PeriodPickersProps = {
  year: string;
  month: string;
  yearOptions: string[];
  countsByYm: Map<string, number>;
  disabled?: boolean;
  onYearChange: (year: string) => void;
  onMonthChange: (month: string) => void;
};

function PufPeriodPickers({
  year,
  month,
  yearOptions,
  countsByYm,
  disabled,
  onYearChange,
  onMonthChange,
}: PeriodPickersProps) {
  return (
    <div className="puf-page__period">
      <label className="puf-page__month-field">
        <span>Year</span>
        <select
          className="field-input puf-page__year-select"
          value={year}
          disabled={disabled}
          onChange={(e) => onYearChange(e.target.value)}
          aria-label="Filter by year"
        >
          {yearOptions.map((y) => (
            <option key={y} value={y}>
              {y}
            </option>
          ))}
        </select>
      </label>
      <label className="puf-page__month-field">
        <span>Month</span>
        <select
          className="field-input puf-page__month-select"
          value={month}
          disabled={disabled}
          onChange={(e) => onMonthChange(e.target.value)}
          aria-label="Filter by month"
        >
          {MONTH_VALUES.map((mm) => {
            const count = countsByYm.get(`${year}-${mm}`) ?? 0;
            return (
              <option key={mm} value={mm}>
                {monthShortLabel(mm)}
                {count > 0 ? ` (${count})` : ''}
              </option>
            );
          })}
        </select>
      </label>
    </div>
  );
}

export function PricingUpdateListPage() {
  const { basePath, kind } = usePortal();
  const isDem = kind === 'dem';
  const [rows, setRows] = useState<ApplicationSubmission[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [yearMonth, setYearMonth] = useState(currentPufYearMonth);
  const lastErrorToastRef = useRef<string | null>(null);
  const { push: pushToast, activeToast, dismissToast } = usePageNotifications();

  useEffect(() => {
    if (error && error !== lastErrorToastRef.current) {
      lastErrorToastRef.current = error;
      pushToast(pageFeedbackToToast({ kind: 'error', message: error }));
    }
    if (!error) lastErrorToastRef.current = null;
  }, [error, pushToast]);

  const homePath = basePath || '/forms';
  const listBase = basePath ? `${basePath}/pricing-update` : '/pricing-update';
  const newPath = `${listBase}/new`;
  const { year, month } = parseYearMonth(yearMonth);

  const load = useCallback(async (opts?: { silent?: boolean }) => {
    const silent = Boolean(opts?.silent);
    if (!silent) setLoading(true);
    setError(null);
    try {
      setRows(await fetchPricingUpdateSubmissions());
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load pricing updates.');
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  useFormsSubmissionsRealtime({
    tables: ['pricing_update_submissions'],
    onChange: () => {
      void load({ silent: true });
    },
  });

  const countsByYm = useMemo(() => {
    const map = new Map<string, number>();
    for (const row of rows) {
      const ym = pufYearMonthFromApplicationNo(row.application_no);
      if (!ym) continue;
      map.set(ym, (map.get(ym) ?? 0) + 1);
    }
    return map;
  }, [rows]);

  const yearOptions = useMemo(() => {
    const set = new Set<string>();
    for (const ym of countsByYm.keys()) set.add(ym.slice(0, 4));
    set.add(currentPufYearMonth().slice(0, 4));
    set.add(year);
    return [...set].sort((a, b) => b.localeCompare(a));
  }, [countsByYm, year]);

  const setYear = useCallback((nextYear: string) => {
    setYearMonth((prev) => `${nextYear}-${parseYearMonth(prev).month}`);
  }, []);

  const setMonth = useCallback((nextMonth: string) => {
    setYearMonth((prev) => `${parseYearMonth(prev).year}-${nextMonth}`);
  }, []);

  const monthRows = useMemo(
    () => rows.filter((row) => pufYearMonthFromApplicationNo(row.application_no) === yearMonth),
    [rows, yearMonth],
  );

  const filteredRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    // Search is global (all months); month filter only applies when the box is empty.
    let list = q ? rows : monthRows;
    if (statusFilter !== 'all') {
      list = list.filter((row) => row.status === statusFilter);
    }
    if (q) {
      const tokens = q.split(/\s+/).filter(Boolean);
      list = list.filter((row) => {
        const blob = pufSearchBlob(row);
        return tokens.every((token) => blob.includes(token));
      });
    }
    return [...list].sort((a, b) => b.submitted_at.localeCompare(a.submitted_at));
  }, [rows, monthRows, search, statusFilter]);

  const stats = useMemo(() => {
    let pending = 0;
    let processing = 0;
    for (const row of monthRows) {
      if (row.status === 'pending_approval' || row.status === 'new') pending += 1;
      if (row.status === 'processing') processing += 1;
    }
    return { total: monthRows.length, pending, processing };
  }, [monthRows]);

  const hasActiveFilters = Boolean(search.trim()) || statusFilter !== 'all';

  const clearFilters = () => {
    setSearch('');
    setStatusFilter('all');
    setYearMonth(currentPufYearMonth());
  };

  const periodPickers = (
    <PufPeriodPickers
      year={year}
      month={month}
      yearOptions={yearOptions}
      countsByYm={countsByYm}
      disabled={loading}
      onYearChange={setYear}
      onMonthChange={setMonth}
    />
  );
  const statusSelect = (
    <select
      className="field-input puf-page__status-filter"
      value={statusFilter}
      onChange={(e) => setStatusFilter(e.target.value)}
      aria-label="Filter by status"
    >
      <option value="all">All statuses</option>
      <option value="new">New</option>
      <option value="processing">Processing</option>
      <option value="pending_approval">Pending approval</option>
      <option value="approved">Approved</option>
      <option value="rejected">Rejected</option>
    </select>
  );

  const pufInlineKpis = (
    <div className="puf-inline-kpis" aria-label="Month summary">
      <div className="puf-inline-kpi">
        <span className="puf-inline-kpi__label">Total · {monthLabel(yearMonth)}</span>
        <span className="puf-inline-kpi__value">{stats.total}</span>
      </div>
      <div className="puf-inline-kpi">
        <span className="puf-inline-kpi__label">New / pending</span>
        <span className="puf-inline-kpi__value">{stats.pending}</span>
      </div>
      <div className="puf-inline-kpi">
        <span className="puf-inline-kpi__label">Processing</span>
        <span className="puf-inline-kpi__value">{stats.processing}</span>
      </div>
    </div>
  );

  return (
    <div className={`puf-page${isDem ? ' puf-page--floating dem-list-scroll' : ''}`}>
      {isDem ? (
        <div className="dem-floating-page-actions">
          {pufInlineKpis}
          <div className="dem-floating-page-actions__btns">
            <button
              type="button"
              className="btn-secondary btn-sm"
              disabled={loading}
              onClick={() => void load()}
            >
              Refresh
            </button>
            <Link to={newPath} className="btn-primary">
              New request
            </Link>
          </div>
        </div>
      ) : (
        <>
          <PageHeader
            title="Pricing Update"
            description="Price update requests (PUF) — requestor, authorised users and approvers."
            actions={
              <div className="puf-page__header-actions">
                <Link to={homePath} className="btn-secondary">
                  All forms
                </Link>
                <Link to={newPath} className="btn-primary">
                  + New request
                </Link>
              </div>
            }
          />
          <div className="kpi-grid dashboard-kpis">
            <div className="card kpi-card">
              <div className="kpi-label">Total · {monthLabel(yearMonth)}</div>
              <div className="kpi-value">{stats.total}</div>
            </div>
            <div className="card kpi-card">
              <div className="kpi-label">New / pending approval</div>
              <div className="kpi-value">{stats.pending}</div>
            </div>
            <div className="card kpi-card">
              <div className="kpi-label">Processing</div>
              <div className="kpi-value">{stats.processing}</div>
            </div>
          </div>
        </>
      )}

      <PageToolbar
        search={search}
        onSearchChange={setSearch}
        searchPlaceholder="Search by ref, customer, item or requestor"
        leading={
          isDem ? (
            <div className="puf-page__toolbar-aside puf-page__toolbar-aside--leading">
              <div className="puf-page__toolbar-controls">
                {periodPickers}
                <span className="puf-page__toolbar-divider" aria-hidden />
                {statusSelect}
              </div>
            </div>
          ) : undefined
        }
        action={
          isDem ? undefined : (
            <div className="puf-page__toolbar-aside">
              <div className="puf-page__toolbar-controls">
                {periodPickers}
                <span className="puf-page__toolbar-divider" aria-hidden />
                {statusSelect}
                <button
                  type="button"
                  className="btn-secondary btn-sm"
                  disabled={loading}
                  onClick={() => void load()}
                >
                  Refresh
                </button>
              </div>
            </div>
          )
        }
      />

      {activeToast
        ? createPortal(
            <PageNotificationToast notification={activeToast} onDismiss={dismissToast} />,
            document.body,
          )
        : null}

      {loading && rows.length === 0 ? (
        <div className="empty-state">
          <div className="empty-state__spinner" />
          <p>Loading pricing updates…</p>
        </div>
      ) : rows.length === 0 ? (
        <div className="empty-state">
          <p>No pricing update requests yet.</p>
          <Link to={newPath} className="btn-primary btn-sm" style={{ marginTop: '0.75rem' }}>
            {isDem ? 'New request' : '+ New request'}
          </Link>
        </div>
      ) : filteredRows.length === 0 ? (
        <div className="empty-state">
          <p>
            {hasActiveFilters
              ? 'No requests match your filters.'
              : `No pricing updates for ${monthLabel(yearMonth)}.`}
          </p>
          <button type="button" className="btn-secondary btn-sm" onClick={clearFilters}>
            Clear filters
          </button>
        </div>
      ) : (
        <div className="puf-list card">
          <div className="dem-table-scroll">
            <div className="puf-list__head" role="row">
              <span>Ref.</span>
              <span>Customer</span>
              <span>Items</span>
              <span>Submitted</span>
              <span>Updated</span>
              <span>Approver</span>
            </div>
            {filteredRows.map((row) => (
              <PricingUpdateListRow
                key={row.id}
                row={row}
                detailPath={`${listBase}/${row.id}`}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
